import { SubscriptionStatus } from './couch-user';
import { isWebBilledPaymentFailing } from './subscription-channel-rules';
import { isInGrace, expiresMillis } from './license-reconciler';

/**
 * The pure decisions behind the two coach billing notices (#4455).
 *
 * A coach is a user who trains other users and pays us for that. Two moments
 * change their money situation and used to pass in silence: their coaching
 * payment stopped working, and the number of seats they pay for changed
 * because a client joined or left.
 *
 * Everything here is pure — no CouchDB, no Paddle, no Angular — so the wording
 * choice and the do-not-repeat rule can be tested directly. The sending lives
 * in `server/functions/src/helpers/coach-billing-notify.ts`, and the in-app
 * cards read the same values from the coach's own record.
 *
 * Full design: `plans/coach-billing-notifications.md`.
 */

/**
 * Which way the coach pays for coaching. The two rails behave differently
 * enough that almost every rule below asks this first.
 *
 * - `paddleSeat` — one Paddle subscription whose quantity is `clients + 1`.
 * - `storeTier` — a fixed monthly Apple / Google plan for a fixed client cap.
 */
export type CoachBillingRail = 'paddleSeat' | 'storeTier';

/**
 * Which of the three wordings the coach gets.
 *
 * The split exists for one reason, and it is the easiest thing here to get
 * wrong: **on the app-store rail we can never honestly say a payment failed.**
 * `coach-tier-webhook-helpers.ts` maps four different Google Play events —
 * `SubscriptionCanceled`, `SubscriptionOnHold`, `SubscriptionInGracePeriod`
 * and `SubscriptionPaused` — onto the single `Suspended` status. One of those
 * is a coach deliberately cancelling. So a message naming a failed payment
 * would be a lie for some of the coaches who saw it. See
 * `design-docs/coach-subscription-lapse.md`, which records this trap.
 */
export type CoachPaymentNoticeKind =
  /** The charge was declined and we can say so. Paddle rail only. */
  | 'payment-failed'
  /** Neutral, and true whether the payment failed or the coach cancelled. */
  | 'not-renewing'
  /** The plan is not active at all — the clients have no access right now. */
  | 'stopped';

export interface CoachPaymentNoticeInput {
  rail: CoachBillingRail;
  /** The status on the coach's own coaching block, after the webhook applied. */
  subscriptionStatus: SubscriptionStatus;
  /** Whether a cancellation is already scheduled on the Paddle subscription. */
  hasScheduledCancel: boolean;
  /**
   * The status this notice last announced to the coach, as stored on their own
   * block. `undefined` when nothing has been announced yet.
   */
  lastAnnouncedStatus?: SubscriptionStatus;
  /**
   * Whether the block has ALREADY stopped granting access — a `Suspended`
   * status whose expiry is in the past. See `resolveCoachNoticeKind`, which
   * turns this into the `stopped` wording so the message cannot quote a spent
   * date as a future one. Defaults to false, which is right for the common case
   * of a block that is still inside its grace window.
   */
  accessAlreadyEnded?: boolean;
}

export interface CoachPaymentNoticeDecision {
  /** Whether to send the coach a message now. */
  announce: boolean;
  /** Which wording. Only meaningful when `announce` is true. */
  kind?: CoachPaymentNoticeKind;
  /**
   * Whether the stored "already announced" marker should be erased, because the
   * subscription is healthy again. Kept separate from `announce` so the caller
   * writes to the user record only when something actually needs recording —
   * an ordinary monthly renewal must not touch the document.
   */
  clearMarker: boolean;
}

const NOT_ANNOUNCING: CoachPaymentNoticeDecision = { announce: false, clearMarker: false };

/**
 * Statuses that mean the coaching plan is in trouble and the coach should hear
 * about it. Written as an allow-list rather than "anything that is not Active",
 * because the statuses it deliberately leaves out are the ones that would
 * otherwise generate a wrong message: `Waiting` and `Temporary` are a checkout
 * in progress, and `None` is a coach who never had a coaching plan at all.
 */
function isAnnounceableStatus(status: SubscriptionStatus): boolean {
  return status === SubscriptionStatus.Suspended
    || status === SubscriptionStatus.Paused
    || status === SubscriptionStatus.Terminated;
}

/**
 * Whether to tell the coach about a problem with their coaching payment, and in
 * which of the three wordings.
 *
 * `Paused` is in the announceable set on purpose. It closes limit 1 recorded in
 * `design-docs/coach-subscription-lapse.md`: `isInGrace` deliberately excludes
 * `Paused`, so the existing client-facing card can never fire for a paused
 * coach and their clients lose access with no warning whatsoever.
 */
export function decideCoachPaymentNotice(input: CoachPaymentNoticeInput): CoachPaymentNoticeDecision {
  const { rail, subscriptionStatus, hasScheduledCancel, lastAnnouncedStatus, accessAlreadyEnded } = input;

  if (!isAnnounceableStatus(subscriptionStatus)) {
    // Healthy again (or never unhealthy). Forget what we announced, so that a
    // coach who fails, pays, and fails again next month is told both times —
    // but only bother writing when there is actually a marker to erase.
    return { announce: false, clearMarker: lastAnnouncedStatus !== undefined };
  }

  // Already told them about exactly this. A status that moved on to a different
  // bad one (dunning ran out) is new information and still announces.
  if (lastAnnouncedStatus === subscriptionStatus) {
    return NOT_ANNOUNCING;
  }

  return {
    announce: true,
    kind: resolveCoachNoticeKind(rail, subscriptionStatus, hasScheduledCancel, accessAlreadyEnded),
    clearMarker: false,
  };
}

/**
 * Which wording a given rail and status calls for.
 *
 * Exported because the in-app card needs exactly the same answer as the sent
 * message. Two copies of this would drift, and the direction they would drift
 * in is the dangerous one: an app card claiming a payment failed on a rail that
 * cannot know that.
 */
export function resolveCoachNoticeKind(
  rail: CoachBillingRail,
  status: SubscriptionStatus,
  hasScheduledCancel: boolean,
  accessAlreadyEnded = false,
): CoachPaymentNoticeKind {
  // Paused and Terminated both mean the plan is not granting anything right
  // now, whichever rail it is on, so neither can be described as a renewal
  // problem.
  if (status === SubscriptionStatus.Paused || status === SubscriptionStatus.Terminated) {
    return 'stopped';
  }

  // The status still looks like a plan running on borrowed time, but the
  // borrowed time is spent — a `Suspended` block whose expiry is already in the
  // past. `isInGrace` is an allow-list of Active / Suspended / Temporary, so
  // this is the ONLY way a granting-looking status can have stopped granting,
  // and it is reachable: a Google Play account hold writes `Suspended` without
  // extending the expiry, so a coach sits in it for up to 30 days.
  //
  // Without this the other two wordings would quote that past date as a future
  // one ("if it does not go through, your access ends on <a day last week>"),
  // and every caller deriving tense from the wording would put access the coach
  // has already lost in the future tense.
  if (accessAlreadyEnded) return 'stopped';

  // Suspended. Only the Paddle rail can distinguish a declined charge from a
  // deliberate cancellation, and the app already has exactly one rule for that
  // — reuse it rather than restating it, so the two cannot drift apart.
  const paymentReallyFailed = rail === 'paddleSeat'
    && isWebBilledPaymentFailing('paddle', status, hasScheduledCancel);
  return paymentReallyFailed ? 'payment-failed' : 'not-renewing';
}

/** The coach block that currently decides how a coach pays, plus which rail it
 *  belongs to. `undefined` when the coach has no billing rail at all — a legacy
 *  admin-granted coach, or somebody who is not a coach. */
export interface CoachBillingRailState {
  rail: CoachBillingRail;
  subscriptionStatus: SubscriptionStatus;
  expiresDate_utc?: string;
}

/** The block shapes this reads. Structurally satisfied by `CoachRailBlocks`
 *  and by the login result the app holds, so callers pass either straight in. */
export interface CoachRailBlocksLike {
  coachTierData?: { subscriptionStatus: SubscriptionStatus; expiresDate_utc?: string };
  coachSeatData?: { subscriptionStatus: SubscriptionStatus; expiresDate_utc?: string };
}

/**
 * Which rail a coach is actually paying on right now.
 *
 * A coach mid-migration between the app store and Paddle can hold BOTH blocks,
 * and reporting the dead one would tell a paying coach their plan had stopped.
 * The order of preference is therefore:
 *
 * 1. a block that is **healthy** — `Active` — because a coach with one live
 *    plan and one ailing one is not in trouble, and saying so would be a false
 *    alarm about money;
 * 2. failing that, a block that is still **granting**, which includes
 *    `Suspended` — that is a plan in dunning, and it is what this rule exists
 *    to report;
 * 3. failing that, whichever ran out LAST, because that is the subscription the
 *    coach most recently had.
 *
 * Step 1 is not a refinement of step 2, it is load-bearing. `isInGrace` counts
 * `Suspended` as granting, so without it a coach paying happily on a store tier
 * who still carries a lingering suspended Paddle seat inside its grace window
 * would be told their payment had failed — decided purely by which block this
 * function happens to look at first.
 *
 * Note this deliberately does not use `resolveCoachLicenseMirror`. That answers
 * a different question — what a coach's CLIENTS should hold — and folds a
 * legacy coach's blocks into a `no-rail` answer that hides the very status this
 * needs to report.
 */
export function resolveCoachBillingRailState(
  blocks: CoachRailBlocksLike | null | undefined,
  nowMillis: number,
): CoachBillingRailState | undefined {
  if (!blocks) return undefined;

  const candidates: CoachBillingRailState[] = [];
  if (blocks.coachSeatData) {
    candidates.push({
      rail: 'paddleSeat',
      subscriptionStatus: blocks.coachSeatData.subscriptionStatus,
      expiresDate_utc: blocks.coachSeatData.expiresDate_utc,
    });
  }
  if (blocks.coachTierData) {
    candidates.push({
      rail: 'storeTier',
      subscriptionStatus: blocks.coachTierData.subscriptionStatus,
      expiresDate_utc: blocks.coachTierData.expiresDate_utc,
    });
  }
  if (candidates.length === 0) return undefined;

  const granting = candidates.filter(c => isInGrace(c.subscriptionStatus, c.expiresDate_utc, nowMillis));
  const healthy = granting.find(c => c.subscriptionStatus === SubscriptionStatus.Active);
  if (healthy) return healthy;
  if (granting.length > 0) return granting[0];

  // None grants. Prefer whichever expired most recently — the coach's latest
  // subscription, rather than one they left behind months ago.
  return [...candidates].sort((a, b) => expiresMillis(b.expiresDate_utc) - expiresMillis(a.expiresDate_utc))[0];
}

/**
 * Render a Paddle money amount for a reader.
 *
 * Paddle reports money as a **count of the currency's smallest unit** — `5996`
 * for $59.96 — never as a decimal figure. Turning that back into money needs
 * the currency's own exponent, and the obvious shortcut of dividing by 100 is
 * wrong for the currencies that have no minor unit at all: 5996 yen would come
 * out as "¥59.96", a bill about a hundred times smaller than the real one. So
 * the exponent is asked of `Intl` for that specific currency rather than
 * assumed.
 *
 * Returns `undefined` — never a partial or guessed figure — when the amount or
 * the currency is missing or unusable. Callers state the money sentence only
 * when this returns something, so an unreadable amount costs the amount and
 * nothing else. See `describeCoachSeatChange`.
 *
 * `locale` is the READER's language: the same amount reads "€59.96" for an
 * English coach and "€ 59,96" for a Dutch one.
 */
export function formatMinorCurrencyAmount(
  amountMinor: string | undefined,
  currency: string | undefined,
  locale: string,
): string | undefined {
  if (!amountMinor || !currency) return undefined;

  const minorUnits = Number(amountMinor);
  if (!Number.isFinite(minorUnits)) return undefined;

  try {
    const formatter = new Intl.NumberFormat(locale, { style: 'currency', currency });
    // How many decimal places this currency actually uses — 2 for USD, 0 for
    // JPY. Asking the formatter is what keeps the divisor honest.
    const exponent = formatter.resolvedOptions().maximumFractionDigits ?? 2;
    return formatter.format(minorUnits / 10 ** exponent);
  } catch {
    // A malformed currency code makes Intl throw. This runs on a best-effort
    // notification path, so it drops the amount and carries on rather than
    // taking the whole message down with it.
    return undefined;
  }
}

export interface CoachSeatChangeInput {
  /** The billed seat quantity before this change. */
  previousQuantity: number;
  /** The billed seat quantity after it. Includes the coach's own seat #1. */
  newQuantity: number;
  /** When Paddle will next charge, if we could read it. */
  nextBilledAt_utc?: string;
  /** The upcoming charge in the currency's smallest unit, if we could read it. */
  nextBilledAmountMinor?: string;
  /** The currency of that amount, if we could read it. */
  nextBilledCurrency?: string;
}

export interface CoachSeatChangeDescription {
  /** Whether the seat count actually moved and is worth a message. */
  announce: boolean;
  /** Clients the coach now pays for — the billed quantity minus their own seat. */
  clientSeats: number;
  /**
   * Whether a next-payment figure can be stated. False when the Paddle read for
   * the upcoming invoice failed, or came back only half-filled.
   */
  hasAmount: boolean;
}

/**
 * Describe a change in the number of seats a Paddle coach is billed for.
 *
 * The ticket (#4455) asks for two separate messages here — "your seat count
 * changed" and "your renewal will be a different amount". They are one event:
 * the seat count changing is exactly why the amount differs, and both become
 * true at the same instant. So this describes one message that states both.
 *
 * **A missing amount does not suppress the message.** The Paddle read for the
 * upcoming invoice can fail, and losing the whole notice because the
 * nice-to-have half was unavailable is worse than sending the half we are sure
 * of. The caller renders the amount sentence only when `hasAmount` is true, and
 * never invents a figure.
 */
export function describeCoachSeatChange(input: CoachSeatChangeInput): CoachSeatChangeDescription {
  const { previousQuantity, newQuantity } = input;

  // The billed quantity carries the coach's own seat #1, which is not a client.
  // Floored at zero: the quantity is floored at 1 upstream, but an older stored
  // record carrying 0 must not render as "-1 clients".
  const clientSeats = Math.max(0, newQuantity - 1);

  if (newQuantity === previousQuantity) {
    return { announce: false, clientSeats, hasAmount: false };
  }

  // Half an amount is more misleading than none — a figure with no currency
  // cannot be rendered as money at all.
  const hasAmount = input.nextBilledAmountMinor !== undefined
    && input.nextBilledCurrency !== undefined
    && input.nextBilledAt_utc !== undefined;

  return {
    announce: true,
    clientSeats,
    hasAmount,
  };
}
